import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AUTH_LOGGED_OUT_KEY } from '../constants/auth';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        if (token) {
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          try {
            const r = await axios.get('/api/users/me');
            if (!cancelled) {
              localStorage.removeItem(AUTH_LOGGED_OUT_KEY);
              setUser(r.data);
            }
          } catch (error) {
            if (cancelled) return;
            // A temporary connection failure must not erase a valid saved session.
            if ([401, 403].includes(error?.response?.status)) {
              localStorage.removeItem('token');
              delete axios.defaults.headers.common['Authorization'];
              setToken(null);
            }
            setUser(null);
          }
        } else {
          if (!cancelled) setUser(null);
        }
      } catch {
        if (!cancelled) {
          localStorage.removeItem('token');
          delete axios.defaults.headers.common['Authorization'];
          setToken(null);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const login = async (email, password) => {
    const { data } = await axios.post('/api/auth/login', { email, password });
    localStorage.removeItem(AUTH_LOGGED_OUT_KEY);
    localStorage.setItem('token', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setToken(data.token);
    setUser(data.user);
  };

  const completeSocialLogin = useCallback(async (code, verifier, state, link) => {
    // A one-time exchange must never be retried by the general API interceptor.
    const { data } = await axios.post('/api/auth/oauth/exchange', { code, verifier, state, ...(link ? { link_existing: true, password: link.password } : {}) }, { __retried: true });
    localStorage.removeItem(AUTH_LOGGED_OUT_KEY);
    localStorage.setItem('token', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setToken(data.token);
    setUser(data.user);
  }, []);

  const register = async (form) => {
    const { data } = await axios.post('/api/auth/register', form, { __retried: true });
    localStorage.removeItem(AUTH_LOGGED_OUT_KEY);
    localStorage.setItem('token', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setToken(data.token);
    setUser(data.user);
  };

  // ゲストアカウントを正式アカウントに昇格（メール・パスワード登録）
  const upgradeGuest = async (form) => {
    const { data } = await axios.post('/api/auth/upgrade-guest', form);
    localStorage.removeItem(AUTH_LOGGED_OUT_KEY);
    setUser(data.user);
  };

  const guestLogin = async () => {
    const { data } = await axios.post('/api/auth/guest');
    localStorage.removeItem(AUTH_LOGGED_OUT_KEY);
    localStorage.setItem('token', data.token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    setToken(data.token);
    setUser(data.user);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.setItem(AUTH_LOGGED_OUT_KEY, '1');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setLoading(false);
  };

  const refreshMe = async () => {
    const { data } = await axios.get('/api/users/me');
    setUser(data);
  };

  const deleteAccount = async (password) => {
    await axios.delete('/api/users/me', { data: { password, confirmation: password === '削除' ? '削除' : undefined }, __retried: true });
    localStorage.removeItem('token');
    localStorage.setItem(AUTH_LOGGED_OUT_KEY, '1');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, completeSocialLogin, upgradeGuest, guestLogin, logout, refreshMe, deleteAccount, setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
